import boulevardCity from "@images/investor-relations/projects/boulevard-city.webp";
import dubaiMall from "@images/investor-relations/projects/dubai-mall.webp";
import fourSeasonsTower from "@images/investor-relations/projects/four-seasons-tower.webp";
import haramMataf from "@images/investor-relations/projects/haram-mataf.webp";
import ithra from "@images/investor-relations/projects/ithra.webp";
import kingdomTower from "@images/investor-relations/projects/kingdom-tower.webp";
import maerskLogisticsPark from "@images/investor-relations/projects/maersk-logistics-park.webp";
import majdoulTower from "@images/investor-relations/projects/majdoul-tower.webp";
import prophetsMosque from "@images/investor-relations/projects/prophets-mosque.webp";
import riyadhMetro from "@images/investor-relations/projects/riyadh-metro.webp";
import sabic from "@images/investor-relations/projects/sabic.webp";
import samiLand from "@images/investor-relations/projects/sami-land.webp";
import spimaco from "@images/investor-relations/projects/spimaco.webp";
import tadawulTower from "@images/investor-relations/projects/tadawul-tower.webp";
import theMove from "@images/investor-relations/projects/the-move.webp";
import westAvenue from "@images/investor-relations/projects/west-avenue.webp";
import zayedInternationalAirport from "@images/investor-relations/projects/zayed-international-airport.webp";

/**
 * Project Highlights — Section 09 of the approved IR content sheet.
 *
 * Cards show the project image, name, city, country and a badge per sector.
 * The content sheet explicitly forbids descriptions, values, clients,
 * consultants, dates and scope.
 *
 * The catalog and the tab membership below come from the delivered asset drop
 * (`references/new-ui/IR-with dimantion/`), whose per-subsidiary folders —
 * `HVAC`, `ZS`, `GIG`, `Zodcon` — supersede the narrower selection in the
 * content sheet's "Project Highlights" tab. One image per project, reused
 * across tabs.
 *
 * Names and locations follow the client's bilingual sheet
 * (`references/new-ui/SENAAT_Project_Names_and_Locations_Bilingual_Numbered_Branded.docx`),
 * which is the authority for both languages and supersedes the earlier
 * per-project guesses.
 */
export type Project = {
  key: string;
  /** Translation key for the project name. */
  name: string;
  /**
   * Translation key for the city. Optional — the card drops the location line
   * for the projects whose location no approved source records yet.
   */
  city?: string;
  /** Translation key for the country. Optional, see `city`. */
  country?: string;
  image: string;
};

export type ProjectTab = {
  key: string;
  /** Translation key for the tab label, reused as the card's sector badge. */
  label: string;
  /** Project keys, in the approved order. */
  projects: string[];
  /**
   * Sector page this tab's "view all" link points at. There is no
   * all-projects page, so the `all` tab deliberately has none and hides the
   * link entirely.
   */
  sectorSlug?: string;
};

const SAUDI_ARABIA = "projectHighlights.countries.saudiArabia";
const UAE = "projectHighlights.countries.unitedArabEmirates";
const BAHRAIN = "projectHighlights.countries.bahrain";

export const projectCatalog: Record<string, Project> = {
  ithra: {
    key: "ithra",
    name: "projectHighlights.projects.ithra",
    city: "projectHighlights.cities.dhahran",
    country: SAUDI_ARABIA,
    image: ithra,
  },
  riyadhMetro: {
    key: "riyadhMetro",
    name: "projectHighlights.projects.riyadhMetro",
    city: "projectHighlights.cities.riyadh",
    country: SAUDI_ARABIA,
    image: riyadhMetro,
  },
  kingdomTower: {
    key: "kingdomTower",
    name: "projectHighlights.projects.kingdomTower",
    city: "projectHighlights.cities.riyadh",
    country: SAUDI_ARABIA,
    image: kingdomTower,
  },
  majdoulTower: {
    key: "majdoulTower",
    name: "projectHighlights.projects.majdoulTower",
    city: "projectHighlights.cities.riyadh",
    country: SAUDI_ARABIA,
    image: majdoulTower,
  },
  dubaiMall: {
    key: "dubaiMall",
    name: "projectHighlights.projects.dubaiMall",
    city: "projectHighlights.cities.dubai",
    country: UAE,
    image: dubaiMall,
  },
  haramMataf: {
    key: "haramMataf",
    name: "projectHighlights.projects.haramMataf",
    city: "projectHighlights.cities.makkah",
    country: SAUDI_ARABIA,
    image: haramMataf,
  },
  prophetsMosque: {
    key: "prophetsMosque",
    name: "projectHighlights.projects.prophetsMosque",
    city: "projectHighlights.cities.madinah",
    country: SAUDI_ARABIA,
    image: prophetsMosque,
  },
  westAvenue: {
    key: "westAvenue",
    name: "projectHighlights.projects.westAvenue",
    city: "projectHighlights.cities.dammam",
    country: SAUDI_ARABIA,
    image: westAvenue,
  },
  theMove: {
    key: "theMove",
    name: "projectHighlights.projects.theMove",
    city: "projectHighlights.cities.jubail",
    country: SAUDI_ARABIA,
    image: theMove,
  },
  tadawulTower: {
    key: "tadawulTower",
    name: "projectHighlights.projects.tadawulTower",
    city: "projectHighlights.cities.riyadh",
    country: SAUDI_ARABIA,
    image: tadawulTower,
  },

  /*
   * Added by the asset drop, which shipped images only. The content sheet has
   * no rows for any of these; their names and locations come from the client's
   * bilingual project sheet (see the header note).
   */
  boulevardCity: {
    key: "boulevardCity",
    name: "projectHighlights.projects.boulevardCity",
    city: "projectHighlights.cities.riyadh",
    country: SAUDI_ARABIA,
    image: boulevardCity,
  },
  fourSeasonsTower: {
    key: "fourSeasonsTower",
    name: "projectHighlights.projects.fourSeasonsTower",
    city: "projectHighlights.cities.manama",
    country: BAHRAIN,
    image: fourSeasonsTower,
  },
  // Inside the Jeddah Islamic Port, on the Red Sea coast.
  maerskLogisticsPark: {
    key: "maerskLogisticsPark",
    name: "projectHighlights.projects.maerskLogisticsPark",
    city: "projectHighlights.cities.jeddah",
    country: SAUDI_ARABIA,
    image: maerskLogisticsPark,
  },
  sabic: {
    key: "sabic",
    name: "projectHighlights.projects.sabic",
    city: "projectHighlights.cities.jubail",
    country: SAUDI_ARABIA,
    image: sabic,
  },
  // The drop's `all` folder carries it but none of the subsidiary folders do;
  // Steel and Construction were confirmed by the client.
  samiLand: {
    key: "samiLand",
    name: "projectHighlights.projects.samiLand",
    city: "projectHighlights.cities.alKharj",
    country: SAUDI_ARABIA,
    image: samiLand,
  },
  // The SPIMACO Addwaeih Office Tower, in Riyadh's commercial district.
  spimaco: {
    key: "spimaco",
    name: "projectHighlights.projects.spimaco",
    city: "projectHighlights.cities.riyadh",
    country: SAUDI_ARABIA,
    image: spimaco,
  },
  zayedInternationalAirport: {
    key: "zayedInternationalAirport",
    name: "projectHighlights.projects.zayedInternationalAirport",
    city: "projectHighlights.cities.abuDhabi",
    country: UAE,
    image: zayedInternationalAirport,
  },
};

export const projectTabs: ProjectTab[] = [
  {
    // Every project in the drop, led by the five the content sheet approved
    // for this tab.
    key: "all",
    label: "projectHighlights.tabs.all",
    projects: [
      "ithra",
      "riyadhMetro",
      "kingdomTower",
      "majdoulTower",
      "dubaiMall",
      "boulevardCity",
      "fourSeasonsTower",
      "haramMataf",
      "maerskLogisticsPark",
      "prophetsMosque",
      "sabic",
      "samiLand",
      "spimaco",
      "tadawulTower",
      "theMove",
      "westAvenue",
      "zayedInternationalAirport",
    ],
  },
  {
    // Zamil Air Conditioners — the drop's `HVAC` folder.
    key: "hvac",
    label: "projectHighlights.tabs.hvac",
    projects: [
      "boulevardCity",
      "haramMataf",
      "ithra",
      "prophetsMosque",
      "sabic",
      "tadawulTower",
      "zayedInternationalAirport",
    ],
    sectorSlug: "air-conditioning-sector",
  },
  {
    // Zamil Steel — the drop's `ZS` folder, plus Sami Land.
    key: "steel",
    label: "projectHighlights.tabs.steel",
    projects: [
      "ithra",
      "riyadhMetro",
      "samiLand",
      "spimaco",
      "theMove",
      "westAvenue",
    ],
    sectorSlug: "fabricated-sector",
  },
  {
    // Gulf Insulation Group — the drop's `GIG` folder.
    key: "insulation",
    label: "projectHighlights.tabs.insulation",
    projects: [
      "dubaiMall",
      "fourSeasonsTower",
      "kingdomTower",
      "majdoulTower",
      "tadawulTower",
    ],
    sectorSlug: "insulation-sector",
  },
  {
    // ZODCON — the drop's `Zodcon` folder, plus Sami Land.
    key: "construction",
    label: "projectHighlights.tabs.construction",
    projects: [
      "haramMataf",
      "ithra",
      "maerskLogisticsPark",
      "riyadhMetro",
      "samiLand",
      "spimaco",
      "theMove",
      "westAvenue",
    ],
    sectorSlug: "construction-sector",
  },
];

export function getProjectTab(tabKey: string): ProjectTab {
  return projectTabs.find(({ key }) => key === tabKey) ?? projectTabs[0];
}

export function getTabProjects(tabKey: string): Project[] {
  return getProjectTab(tabKey)
    .projects.map((projectKey) => projectCatalog[projectKey])
    .filter(Boolean);
}

/**
 * Sector tabs a project belongs to, which drives its card badges. Derived from
 * `projectTabs` rather than stored per project, so a badge can never drift out
 * of sync with the tab that filters the card in.
 */
export function getProjectSectors(projectKey: string): ProjectTab[] {
  return projectTabs.filter(
    ({ key, projects }) => key !== "all" && projects.includes(projectKey),
  );
}
